/* eslint-disable react/no-unused-prop-types */
import React from "react";
import PropTypes from "prop-types";

import { gatherClasses, isFunction } from "../../lib/utils";
import Row from "../row/row";
import Col from "../col/col";
import Icon from "../icon/icon";
import Modal from "../modal/modal";
import Button from "../button/button";
import Alert from "../alert/alert";
import { WizardArrow } from "../../lib/icons";
import "./wizard.scss";

const getWizardBodyHeight = height => {
  return String(height).endsWith("px") ? height : `${height}px`;
};

const renderWizard = props => {
  return (
    <div
      className={gatherClasses(props, "oui-wizard-container")}
      style={
        props.size !== "xxl" && props.bodyHeight
          ? { height: getWizardBodyHeight(props.bodyHeight) }
          : {}
      }
    >
      {props.alert && props.alert.show ? (
        <Alert
          type={props.alert.type}
          message={props.alert.message}
          closable={props.alert.closable}
          onClose={props.alert.onClose}
        />
      ) : null}
      <div className="oui-wizard-steps-container">
        <div className="oui-wizard-steps">
          {props.items.map((item, index) => {
            return (
              <div
                key={index}
                className={gatherClasses(
                  null,
                  "oui-wizard-step",
                  "oui-text-center",
                  props.currentStepIndex === index ? "oui-current-step" : null,
                  index < props.currentStepIndex ? "oui-completed-step" : null
                )}
              >
                <div className="oui-step-title">{item.title}</div>
                {index !== props.items.length - 1 ? (
                  <Icon
                    src={WizardArrow}
                    className="oui-step-separator"
                    width="12px"
                    height="12px"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={gatherClasses(
          null,
          "oui-wizard-step-components",
          props.wizardBodyClassName || ""
        )}
      >
        {props.items.map((item, index) => {
          return (
            <div
              className={gatherClasses(
                null,
                "oui-wizard-step-component",
                props.currentStepIndex === index ? "oui-active" : null
              )}
              key={index}
            >
              {isFunction(item.component) ? item.component() : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const wizardModalBody = props => {
  return renderWizard(props);
};

const wizardModalFooter = props => {
  return (
    <Row className="oui-text-left oui-flex-fill">
      <Col>
        {props.currentStepIndex === 0 ? null : (
          <Button
            label={props.prevLabel}
            onClick={props.onPrevClick}
            disabled={props.disablePrev}
          />
        )}
      </Col>
      <Col className="oui-text-right">
        <Button label="Cancel" onClick={props.toggle} />
        {props.currentStepIndex === props.items.length - 1 ? null : (
          <Button
            label={props.nextLabel}
            type="primary"
            onClick={props.onNextClick}
            disabled={props.disableNext}
          />
        )}
        {props.currentStepIndex !== props.items.length - 1 ? null : (
          <Button
            label={props.finishLabel}
            type="primary"
            onClick={props.onFinishClick}
            disabled={props.disableFinish}
          />
        )}
      </Col>
    </Row>
  );
};

const Wizard = props => {
  const {
    dataTestId,
    size,
    isOpen,
    toggle,
    header,
    docUrl,
    closeOnEscape,
    onOpened,
    onClosed,
    bodyClassName,
    docUrlTooltipText
  } = props;
  return (
    <Modal
      dataTestId={dataTestId}
      size={size}
      className="oui-modal-wizard"
      isOpen={isOpen}
      toggle={toggle}
      header={header}
      docUrl={docUrl}
      body={() => wizardModalBody(props)}
      footer={() => wizardModalFooter(props)}
      closeOnEscape={closeOnEscape}
      onOpened={onOpened}
      onClosed={onClosed}
      bodyClassName={bodyClassName || null}
      docUrlTooltipText={docUrlTooltipText}
    />
  );
};

Wizard.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      component: PropTypes.func
    })
  ).isRequired,
  currentStepIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "xxl"]),
  onPrevClick: PropTypes.func,
  onNextClick: PropTypes.func,
  onFinishClick: PropTypes.func,
  header: PropTypes.string,
  docUrl: PropTypes.string,
  alert: PropTypes.shape({
    type: PropTypes.string,
    show: PropTypes.bool,
    closable: PropTypes.bool,
    onClose: PropTypes.func,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
      .isRequired
  }),
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  wizardBodyClassName: PropTypes.string,
  disableFinish: PropTypes.bool,
  disableNext: PropTypes.bool,
  disablePrev: PropTypes.bool,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  finishLabel: PropTypes.string,
  bodyHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataTestId: PropTypes.string,
  docUrlTooltipText: PropTypes.string,
  closeOnEscape: PropTypes.bool,
  onOpened: PropTypes.func,
  onClosed: PropTypes.func
};

Wizard.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  items: [],
  // eslint-disable-next-line react/default-props-match-prop-types
  currentStepIndex: 0,
  isOpen: false,
  size: "xxl",
  docUrl: null,
  prevLabel: "Previous",
  nextLabel: "Next",
  finishLabel: "Finish",
  dataTestId: "orion-wizard"
};

Wizard.displayName = "OrionWizard";

export default Wizard;
